import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  // Section headers
  LayoutDashboard, DollarSign, Users, Mail, Zap, Globe, BookOpen, HelpCircle, Settings,
  // Nav items
  BarChart3, TrendingUp, Banknote, ShieldCheck, Archive, Receipt,
  User, Megaphone, ImageIcon,
  Send, LayoutTemplate, MailCheck, CheckCircle, MailOpen, Cpu,
  FileText, SlidersHorizontal,
  Globe2, ScrollText,
  Newspaper, Video, MessageSquare,
  Radio, MessageCircle,
  Key, Database, Terminal, Activity, Bell, CreditCard,
  LogOut, ChevronDown, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';

interface SidebarProps {
  activeTab: string;
  onNotificationClick: () => void;
  alertCount: number;
  isDark?: boolean;
}

const AdminSidebar: React.FC<SidebarProps> = ({ activeTab, alertCount }) => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [adminProfilePicture, setAdminProfilePicture] = useState('');

  const handleTabChange = (tabId: string) => {
    navigate(`/admin/control-panel-7x9k2m/${tabId}`);
  };

  const handleLogout = async () => {
    try {
      await logout();
      const isAdminMode = process.env.REACT_APP_ADMIN_MODE === 'true';
      const isAdminSubdomain = window.location.hostname.startsWith('admin.');
      window.location.href = (isAdminMode || isAdminSubdomain) ? '/login' : '/admin/login';
    } catch (e) {
      console.error('Logout failed:', e);
    }
  };

  const getDisplayName = () => currentUser?.email || 'Admin';

  useEffect(() => {
    const load = () => {
      const s = localStorage.getItem('adminProfilePicture');
      if (s) setAdminProfilePicture(s);
    };
    load();
    const onStorage = (e: StorageEvent) => { if (e.key === 'adminProfilePicture') load(); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('profilePictureUpdated', load);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('profilePictureUpdated', load);
    };
  }, []);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    dashboard: true,
    'revenue-subscription': true,
    email: true,
  });

  const toggle = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const sz = 14; // icon size for nav items
  const hSz = 14; // icon size for section headers

  const categories = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: <LayoutDashboard size={hSz} />,
      items: [
        { id: 'overview',  name: 'Overview',  icon: <BarChart3 size={sz} /> },
        { id: 'analytics', name: 'Analytics', icon: <TrendingUp size={sz} /> },
      ],
    },
    {
      id: 'revenue-subscription',
      name: 'Revenue & Billing',
      icon: <DollarSign size={hSz} />,
      items: [
        { id: 'revenue',                name: 'Revenue',              icon: <Banknote size={sz} /> },
        { id: 'current-subscription',   name: 'Current Subscription', icon: <ShieldCheck size={sz} /> },
        { id: 'cancelled-subscription', name: 'Cancelled',            icon: <Archive size={sz} /> },
        { id: 'transaction',            name: 'Transactions',         icon: <Receipt size={sz} /> },
      ],
    },
    {
      id: 'users-management',
      name: 'Users',
      icon: <Users size={hSz} />,
      items: [
        { id: 'users',     name: 'Users',     icon: <User size={sz} /> },
        { id: 'campaigns', name: 'Campaigns', icon: <Megaphone size={sz} /> },
        { id: 'uploads',   name: 'Uploads',   icon: <ImageIcon size={sz} /> },
      ],
    },
    {
      id: 'email',
      name: 'Email',
      icon: <Mail size={hSz} />,
      items: [
        { id: 'automations',     name: 'Send Email',      icon: <Send size={sz} /> },
        { id: 'email-templates', name: 'Email Templates', icon: <LayoutTemplate size={sz} /> },
        { id: 'system-emails',   name: 'System Emails',   icon: <MailCheck size={sz} /> },
        { id: 'email-service',   name: 'Email Service',   icon: <Cpu size={sz} /> },
        { id: 'email',           name: 'Email Logs',      icon: <MailOpen size={sz} /> },
        { id: 'sender',          name: 'Sender',          icon: <CheckCircle size={sz} /> },
      ],
    },
    {
      id: 'automations',
      name: 'Automations',
      icon: <Zap size={hSz} />,
      items: [
        { id: 'automation-templates', name: 'Automation Templates', icon: <FileText size={sz} /> },
        { id: 'automation-control',   name: 'Automation Control',   icon: <SlidersHorizontal size={sz} /> },
      ],
    },
    {
      id: 'web',
      name: 'Web & Assets',
      icon: <Globe size={hSz} />,
      items: [
        { id: 'templates',    name: 'Landing Templates', icon: <LayoutTemplate size={sz} /> },
        { id: 'signup-forms', name: 'Signup Forms',      icon: <ScrollText size={sz} /> },
        { id: 'dns-checker',  name: 'Check DNS',         icon: <Globe2 size={sz} /> },
      ],
    },
    {
      id: 'content',
      name: 'Content',
      icon: <BookOpen size={hSz} />,
      items: [
        { id: 'news-center',       name: 'News Center',     icon: <Newspaper size={sz} /> },
        { id: 'app-offer-banners', name: 'Offer Banners',   icon: <Megaphone size={sz} /> },
        { id: 'blog-posts',        name: 'Blog Posts',      icon: <FileText size={sz} /> },
        { id: 'video-tutorials',   name: 'Video Tutorials', icon: <Video size={sz} /> },
      ],
    },
    {
      id: 'support',
      name: 'Support',
      icon: <HelpCircle size={hSz} />,
      items: [
        { id: 'help-center', name: 'Help Center',          icon: <MessageSquare size={sz} /> },
        { id: 'feedback',    name: 'Feedback',              icon: <MessageCircle size={sz} /> },
        { id: 'contact-submissions', name: 'Contact Inbox', icon: <Mail size={sz} /> },
        { id: 'status',      name: 'Status Page',           icon: <Radio size={sz} /> },
      ],
    },
    {
      id: 'system',
      name: 'System',
      icon: <Settings size={hSz} />,
      items: [
        { id: 'system-secrets',    name: 'System Secrets',    icon: <Key size={sz} /> },
        { id: 'firebase-database', name: 'Firebase Database', icon: <Database size={sz} /> },
        { id: 'api',               name: 'API Management',    icon: <Terminal size={sz} /> },
        { id: 'api-activity',      name: 'API Activity',      icon: <Activity size={sz} /> },
        { id: 'notifications',     name: 'Notifications',     icon: <Bell size={sz} /> },
        { id: 'plan',              name: 'Plan & Billing',    icon: <CreditCard size={sz} /> },
        { id: 'settings',          name: 'General Settings',  icon: <Settings size={sz} /> },
      ],
    },
  ];

  return (
    <div
      style={{
        width: isCollapsed ? '64px' : '240px',
        backgroundColor: '#D3D3D3',
        fontFamily: 'Inter, sans-serif',
        transition: 'width 0.25s ease',
      }}
      className="border-r border-black/10 h-screen flex flex-col shadow-sm z-20 flex-shrink-0"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="h-16 flex items-center justify-between px-4 border-b border-black/10 flex-shrink-0">
        {!isCollapsed ? (
          <>
            <button
              onClick={() => navigate('/admin/control-panel-7x9k2m/overview')}
              className="hover:opacity-70 transition-opacity"
            >
              <img
                src="/Scubamail logo for white bg.png"
                alt="Scubamail"
                className="h-7 w-auto object-contain"
              />
            </button>
            <button
              onClick={() => setIsCollapsed(true)}
              className="p-1.5 rounded-lg text-black/40 hover:text-black hover:bg-black/8 transition-all"
            >
              <PanelLeftClose size={16} />
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsCollapsed(false)}
            className="mx-auto p-1.5 rounded-lg text-black/40 hover:text-black hover:bg-black/8 transition-all"
          >
            <PanelLeftOpen size={16} />
          </button>
        )}
      </header>

      {/* ── Navigation ─────────────────────────────────────────────────────── */}
      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden custom-scrollbar">
        {categories.map((cat) => (
          <div key={cat.id} className={`${isCollapsed ? 'px-1.5' : 'px-2.5'} mb-0.5`}>
            {!isCollapsed ? (
              <>
                {/* Section header button */}
                <button
                  onClick={() => toggle(cat.id)}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-black/5 transition-colors group mb-0.5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-black/40 flex-shrink-0 group-hover:text-black/60 transition-colors">
                      {cat.icon}
                    </span>
                    <span className="text-[10.5px] font-semibold text-black/50 uppercase tracking-[0.07em] truncate group-hover:text-black/70 transition-colors leading-none">
                      {cat.name}
                    </span>
                  </div>
                  <span className={`text-black/30 flex-shrink-0 transition-transform duration-200 ${expanded[cat.id] ? 'rotate-180' : ''}`}>
                    <ChevronDown size={11} />
                  </span>
                </button>

                {/* Items */}
                <div className={`overflow-hidden transition-all duration-200 ease-in-out ${expanded[cat.id] ? 'max-h-[600px]' : 'max-h-0'}`}>
                  {cat.items.map((item) => {
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleTabChange(item.id)}
                        className={`w-full flex items-center gap-2.5 pl-8 pr-3 py-2 rounded-lg mb-px transition-all duration-150 relative ${
                          isActive
                            ? 'bg-black/10 text-black'
                            : 'text-black/55 hover:text-black hover:bg-black/5'
                        }`}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-black rounded-r-full" />
                        )}
                        <span className="flex-shrink-0">{item.icon}</span>
                        <span className={`text-[12.5px] truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>
                          {item.name}
                        </span>
                        {item.id === 'notifications' && alertCount > 0 && (
                          <span className="ml-auto flex-shrink-0 h-4 min-w-[16px] px-1 bg-black text-white text-[9px] font-bold flex items-center justify-center rounded-full">
                            {alertCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              /* Collapsed: icon-only column */
              <div className="flex flex-col items-center gap-0.5 py-2 border-t border-black/8 first:border-0 first:pt-0">
                {cat.items.map((item) => {
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      title={item.name}
                      className={`relative p-2.5 rounded-lg transition-all duration-150 ${
                        isActive
                          ? 'bg-black/10 text-black'
                          : 'text-black/45 hover:text-black hover:bg-black/5'
                      }`}
                    >
                      {item.icon}
                      {item.id === 'notifications' && alertCount > 0 && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-black rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* ── User / Sign Out ─────────────────────────────────────────────────── */}
      <div className={`border-t border-black/10 flex-shrink-0 ${isCollapsed ? 'p-2 flex flex-col items-center gap-1.5' : 'p-3'}`}>
        {/* Avatar row */}
        <div className={`flex items-center gap-2.5 ${isCollapsed ? '' : 'px-1 mb-2'}`}>
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 rounded-xl overflow-hidden bg-black/10 flex items-center justify-center">
              {adminProfilePicture ? (
                <img src={adminProfilePicture} alt="Admin" className="w-full h-full object-cover" />
              ) : (
                <span className="text-[11px] font-bold text-black">
                  {getDisplayName().charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-[#D3D3D3]" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-black truncate leading-tight">{getDisplayName()}</p>
              <p className="text-[10px] text-black/40 font-medium uppercase tracking-wider">Administrator</p>
            </div>
          )}
        </div>

        {/* Sign out */}
        {!isCollapsed ? (
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-black/55 hover:text-black hover:bg-black/5 transition-all"
          >
            <LogOut size={14} />
            <span className="text-[12.5px] font-medium">Sign Out</span>
          </button>
        ) : (
          <button
            onClick={handleLogout}
            title="Sign Out"
            className="p-2.5 rounded-lg text-black/45 hover:text-black hover:bg-black/5 transition-all"
          >
            <LogOut size={14} />
          </button>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.2); }
      `}} />
    </div>
  );
};

export default AdminSidebar;
